import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Menu, X, ChevronDown, Shield, FileText, Building2, Search, ShoppingBag } from 'lucide-react';
import { openZendeskChat } from '../utils/chat';

const Navbar = ({ onOpenModal }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const services = [
    {
      id: 1,
      name: 'Trademark Registration',
      icon: Shield,
      description: 'Protect your brand identity',
      color: 'text-brand-primary',
      bgColor: 'bg-emerald-50',
      path: '/trademark-registration'
    },
    {
      id: 2,
      name: 'Copyright Registration',
      icon: FileText,
      description: 'Secure your creative works',
      color: 'text-brand-primary',
      bgColor: 'bg-blue-50',
      path: '/copyright-registration'
    },
    {
      id: 3,
      name: 'Business Formation',
      icon: Building2,
      description: 'Set up your company legally',
      color: 'text-brand-primary',
      bgColor: 'bg-purple-50',
      path: '/business-formation'
    },
    {
      id: 4,
      name: 'Trademark Search',
      icon: Search,
      description: 'Avoid potential conflicts',
      color: 'text-brand-primary',
      bgColor: 'bg-orange-50',
      path: '/trademark-search'
    },
    {
      id: 5,
      name: 'Amazon Brand Registry',
      icon: ShoppingBag,
      description: 'Protect your Amazon presence',
      color: 'text-brand-primary',
      bgColor: 'bg-teal-50',
      path: '/amazon-brand-registry'
    }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-3' : 'bg-white/95 backdrop-blur-sm shadow-md py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between">
          <div 
            className="cursor-pointer"
            onClick={() => scrollToSection('home')}
          >
            <img 
              src="/assets/img/brand/logo-dark.png" 
              alt="Trademark Law Associates" 
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-brand-primary transition-colors font-medium relative group"
            >
              Home
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-600 transition-all group-hover:w-full"></span>
            </button>
            
            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="text-gray-700 hover:text-brand-primary transition-colors font-medium flex items-center space-x-1 outline-none relative group">
                <span>Services</span>
                <ChevronDown size={16} className="group-hover:translate-y-0.5 transition-transform" />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-600 transition-all group-hover:w-full"></span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-72 p-2 mt-2">
                {services.map((service) => {
                  const Icon = service.icon;
                  return (
                    <DropdownMenuItem
                      key={service.id}
                      onClick={() => {
                        navigate(service.path);
                        setIsMobileMenuOpen(false);
                      }}
                      className="p-3 cursor-pointer rounded-lg hover:bg-gradient-to-r hover:from-emerald-50 hover:to-teal-50 transition-all group"
                    >
                      <div className="flex items-start space-x-3">
                        <div className={`${service.bgColor} p-2 rounded-lg group-hover:scale-110 transition-transform`}>
                          <Icon className={service.color} size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="font-semibold text-gray-800 group-hover:text-brand-primary transition-colors">
                            {service.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {service.description}
                          </div>
                        </div>
                      </div>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuContent>
            </DropdownMenu>

            <button
              onClick={() => navigate('/about-us')}
              className="text-gray-700 hover:text-brand-primary transition-colors font-medium relative group"
            >
              About Us
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-600 transition-all group-hover:w-full"></span>
            </button>
            <button
              onClick={() => navigate('/contact-us')}
              className="text-gray-700 hover:text-brand-primary transition-colors font-medium relative group"
            >
              Contact Us
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-emerald-600 transition-all group-hover:w-full"></span>
            </button>
            <Button
              onClick={openZendeskChat}
              className="bg-brand-gradient text-white hover:opacity-90 shadow-md hover:shadow-lg transition-all font-semibold"
            >
              Live Chat
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 animate-fade-in">
            <button
              onClick={() => scrollToSection('home')}
              className="block w-full text-left px-4 py-2.5 text-gray-700 hover:text-brand-primary hover:bg-emerald-50 rounded-lg transition-colors font-medium"
            >
              Home
            </button>
            
            {/* Mobile Services Dropdown */}
            <div>
              <button
                onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                className="flex items-center justify-between w-full text-left px-4 py-2.5 text-gray-700 hover:text-brand-primary hover:bg-emerald-50 rounded-lg transition-colors font-medium"
              >
                <span>Services</span>
                <ChevronDown 
                  size={16} 
                  className={`transition-transform ${mobileServicesOpen ? 'rotate-180' : ''}`} 
                />
              </button>
              {mobileServicesOpen && (
                <div className="ml-4 mt-2 space-y-1">
                  {services.map((service) => {
                    const Icon = service.icon;
                    return (
                      <button
                        key={service.id}
                        onClick={() => {
                          navigate(service.path);
                          setIsMobileMenuOpen(false);
                        }}
                        className="flex items-center space-x-3 w-full text-left px-3 py-2 text-gray-600 hover:text-brand-primary hover:bg-emerald-50 rounded-lg transition-colors"
                      >
                        <div className={`${service.bgColor} p-1.5 rounded-md`}>
                          <Icon className={service.color} size={16} />
                        </div>
                        <span className="text-sm">{service.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            <button
              onClick={() => {
                navigate('/about-us');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2.5 text-gray-700 hover:text-brand-primary hover:bg-emerald-50 rounded-lg transition-colors font-medium"
            >
              About Us
            </button>
            <button
              onClick={() => {
                navigate('/contact-us');
                setIsMobileMenuOpen(false);
              }}
              className="block w-full text-left px-4 py-2.5 text-gray-700 hover:text-brand-primary hover:bg-emerald-50 rounded-lg transition-colors font-medium"
            >
              Contact Us
            </button>
            <Button
              onClick={() => {
                openZendeskChat();
                setIsMobileMenuOpen(false);
              }}
              className="w-full bg-brand-gradient text-white hover:opacity-90 mt-2 font-semibold"
            >
              Live Chat
            </Button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
